export const ELEMENTS = ["Cu", "Sn", "Pb", "Zn", "Fe", "Sb", "Ni", "Al", "Mn", "Si", "S", "P", "Bi", "Nb"];

export const DEFAULT_MATERIALS = [
    { id: '1', name: 'Pure Copper', composition: { Cu: 100 } },
    { id: '2', name: 'Pure Tin', composition: { Sn: 100 } },
    { id: '3', name: 'Pure Zinc', composition: { Zn: 100 } },
    { id: '4', name: 'Pure Lead', composition: { Pb: 100 } },
    { id: '5', name: 'Al-Fe Master Alloy', composition: { Al: 50, Fe: 50 } },
    { id: '6', name: 'Brass Scrap', composition: { Cu: 60, Zn: 40 } },
    { id: '7', name: 'Pure Aluminum', composition: { Al: 100 } },
    { id: '8', name: 'Ferromanganese', composition: { Fe: 20, Mn: 80 } },
    { id: '9', name: 'Silicon Copper', composition: { Cu: 90, Si: 10 } },
];

export const calculateCharge = (chargeWeight, currentSample, targetComposition, recoveryFactor, materials = DEFAULT_MATERIALS) => {
    const iterations = [];
    const tolerance = 0.05; // 0.05%
    let currentWeight = parseFloat(chargeWeight);

    if (isNaN(currentWeight) || currentWeight <= 0) return { error: "Invalid charge weight" };

    // Initialize current mass for each element
    const currentMass = {};
    ELEMENTS.forEach(el => {
        currentMass[el] = currentWeight * ((parseFloat(currentSample[el]) || 0) / 100);
    });

    const getElementPercent = (el, totalW, masses) => {
        if (totalW === 0) return 0;
        return (masses[el] / totalW) * 100;
    };

    const getBestMaterial = (element) => {
        // Find material with highest concentration of requested element that doesn't push others too high
        let best = null;
        let maxConc = 0;
        for (const mat of materials) {
            const conc = mat.composition[element] || 0;
            if (conc > maxConc) {
                maxConc = conc;
                best = mat;
            }
        }
        return best;
    };

    let isConverged = false;
    let maxIterations = 20;
    let iterCount = 0;
    let materialTotals = {}; // Track total material added

    while (!isConverged && iterCount < maxIterations) {
        iterCount++;
        let addedAnything = false;
        let worstDiff = 0;
        let worstEl = null;

        // Find the element with the largest shortfall
        for (const el of ELEMENTS) {
            const target = parseFloat(targetComposition[el]) || 0;
            const currentPer = getElementPercent(el, currentWeight, currentMass);
            const diff = target - currentPer;

            if (diff > tolerance && diff > worstDiff) {
                worstDiff = diff;
                worstEl = el;
            }
        }

        if (!worstEl) {
            isConverged = true;
            break;
        }

        // Pick best material for worstEl
        const bestMat = getBestMaterial(worstEl);
        if (!bestMat) {
            // If we can't find a material to add the element, just break to avoid infinite loop
            break;
        }

        // Calculate how much of worstEl is needed (in mass)
        // Target mass = Target % * (CurrentWeight + AddedWeight)
        // Since AddedWeight is unknown and changes total weight, we use a simple approximation and iterate
        const targetElMassReq = currentWeight * ((parseFloat(targetComposition[worstEl]) || 0) / 100);
        let shortfallMass = targetElMassReq - currentMass[worstEl];

        // Apply recovery factor
        const rec = (parseFloat(recoveryFactor[worstEl]) || 100) / 100;
        const actualNeeded = shortfallMass / (rec > 0 ? rec : 1);

        // Determine material quantity based on its concentration of worstEl
        const conc = (bestMat.composition[worstEl] || 100) / 100;
        let matAdded = actualNeeded / conc;

        if (matAdded < 0.1) matAdded = 0.1; // enforce small minimums occasionally

        // Record the iteration
        const iterationDetails = {
            iteration: iterCount,
            materialAdded: bestMat.name,
            quantityAddedLbs: matAdded.toFixed(2),
            compositionAfter: {}
        };

        // Update weights and masses
        currentWeight += matAdded;
        if (!materialTotals[bestMat.name]) materialTotals[bestMat.name] = 0;
        materialTotals[bestMat.name] += matAdded;

        Object.keys(bestMat.composition).forEach(compEl => {
            const addedElMass = matAdded * (bestMat.composition[compEl] / 100);
            currentMass[compEl] += addedElMass;
        });

        ELEMENTS.forEach(el => {
            iterationDetails.compositionAfter[el] = getElementPercent(el, currentWeight, currentMass).toFixed(3);
        });

        iterations.push(iterationDetails);
        addedAnything = true;

        // A simple sanity check to break out if adding way too much (e.g. 5x original weight)
        if (currentWeight > chargeWeight * 5) {
            break;
        }
    }

    // Compile final results
    const finalComposition = {};
    const comparison = [];

    ELEMENTS.forEach(el => {
        const finalVal = getElementPercent(el, currentWeight, currentMass);
        const targetVal = parseFloat(targetComposition[el]) || 0;
        finalComposition[el] = finalVal.toFixed(3);

        comparison.push({
            element: el,
            target: targetVal.toFixed(2),
            calculated: finalVal.toFixed(2),
            difference: (finalVal - targetVal).toFixed(2)
        });
    });

    return {
        success: true,
        initialWeight: chargeWeight,
        finalWeight: currentWeight.toFixed(2),
        totalMaterialAdded: (currentWeight - chargeWeight).toFixed(2),
        iterations,
        finalComposition,
        comparison,
        materialTotals: Object.entries(materialTotals).map(([name, qty]) => ({ name, qty: qty.toFixed(2) }))
    };
};
